// src/components/E164PhoneInput.jsx
import React, { useEffect, useMemo, useState } from "react";

/**
 * E164PhoneInput (US-only, no country picker)
 *
 * Props:
 *  - value: string | null   // accepts "+1XXXXXXXXXX" or raw digits; optional
 *  - onChange: (e164OrNull: string | null) => void
 *
 * Behavior:
 *  - Shows a fixed "+1" prefix (no flags, no dropdown).
 *  - Accepts up to 10 digits; emits "+1" + digits (E.164) or null when empty.
 *  - Keeps local state in sync if parent updates `value`.
 */
const E164PhoneInput = ({ value, onChange }) => {
  // Derive initial 10-digit string from incoming value
  const initialDigits = useMemo(() => {
    if (!value) return "";
    const s = String(value).trim();
    if (s.startsWith("+1")) {
      return s.slice(2).replace(/[^\d]/g, "").slice(0, 10);
    }
    return s.replace(/[^\d]/g, "").slice(0, 10);
  }, [value]);

  const [digits, setDigits] = useState(initialDigits);

  // Keep local state synced to external changes (no eslint disable needed)
  useEffect(() => {
    if (digits !== initialDigits) setDigits(initialDigits);
  }, [initialDigits, digits]);

  const handleChange = (e) => {
    const only = e.target.value.replace(/[^\d]/g, "").slice(0, 10);
    setDigits(only);
    if (onChange) onChange(only ? `+1${only}` : null);
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.prefix}>+1</div>
      <input
        type="tel"
        inputMode="numeric"
        pattern="\d*"
        placeholder="Phone number"
        aria-label="US phone number"
        value={digits}
        onChange={handleChange}
        style={styles.input}
      />
    </div>
  );
};

const styles = {
  wrap: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    border: "1px solid #ccc",
    borderRadius: 8,
    overflow: "hidden",
    background: "#fff",
  },
  prefix: {
    padding: "10px 12px",
    background: "#f3f4f6",
    color: "#111827",
    fontSize: 14,
    borderRight: "1px solid #e5e7eb",
    userSelect: "none",
  },
  input: {
    flex: 1,
    padding: "10px 12px",
    border: "none",
    outline: "none",
    fontSize: 14,
    background: "#fff",
  },
};

export default E164PhoneInput;
